import React, { useEffect, useState } from 'react';
import { supabase, Category, JuryVote, Nomination } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import NominationDetails from '../nominations/NominationDetails';
import './Dashboard.css';

interface RankingRow {
  nomination_id: string;
  candidate_name?: string | null;
  project_title?: string | null;
  average_score: number;
  total_votes: number; // jurados que votaron esa candidatura
  total_jurors: number; // jurados asignados en la categoría
  points_total: number; // puntos totales (media * total_votes)
  rank: number;
}

const JuryVotes: React.FC = () => {
  const { user, isNovagobTeam } = useAuth();
  const canViewRankings = isNovagobTeam;
  const [myCategories, setMyCategories] = useState<Category[]>([]);
  const [assignedCategoryIds, setAssignedCategoryIds] = useState<Record<string, true>>({});
  const [rankings, setRankings] = useState<Record<string, RankingRow[]>>({});
  const [juryCounts, setJuryCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedNomination, setSelectedNomination] = useState<Nomination | null>(null);
  const [jurorsVotedByCategory, setJurorsVotedByCategory] = useState<Record<string, number>>({});
  const [approvedCountByCategory, setApprovedCountByCategory] = useState<Record<string, number>>({});
  const [activeJurorsByCategory, setActiveJurorsByCategory] = useState<Record<string, number>>({});
  const [matrixByCategory, setMatrixByCategory] = useState<Record<string, {
    jurors: { id: string; name: string; email: string }[];
    nominations: { id: string; title: string }[];
    votes: Record<string, Set<string>>; // nomId -> set of jurorIds that voted
  }>>({});
  const [matrixCategory, setMatrixCategory] = useState<Category | null>(null);
  const [selectedJuror, setSelectedJuror] = useState<{ id: string; name: string; email: string } | null>(null);
  const [selectedJurorProfile, setSelectedJurorProfile] = useState<any>(null);
  const [voteVotersModal, setVoteVotersModal] = useState<{
    title: string;
    voted: { id: string; name: string; email: string }[];
    pending: { id: string; name: string; email: string }[];
  } | null>(null);
  const [view, setView] = useState<'rankings' | 'charts' | 'finalists'>('rankings');
  const [publicVotesByNomination, setPublicVotesByNomination] = useState<Record<string, number>>({});
  const [totalUniqueVoters, setTotalUniqueVoters] = useState(0);

  useEffect(() => {
    if (user && canViewRankings) {
      loadData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canViewRankings]);

  const loadData = async () => {
    try {
      setLoading(true);

      // En "Votaciones" se muestran TODAS las categorías, no solo las asignadas.
      const { data: categoriesData, error: categoriesError } = await supabase
        .schema('premios')
        .from('categories')
        .select('id, name, type, max_nominations, current_nominations, is_open, created_at, updated_at');

      if (categoriesError) throw categoriesError;

      const categories = (categoriesData || []) as Category[];
      setMyCategories(categories);

      const categoryIds = categories.map((c) => c.id);

      if (categoryIds.length === 0) {
        setRankings({});
        setJuryCounts({});
        return;
      }

      // Número total de jurados asignados por categoría (X/Y)
      const { data: juryMembersRows, error: juryMembersError } = await supabase
        .schema('premios')
        .from('jury_members')
        .select('category_id, user_id')
        .in('category_id', categoryIds);

      if (juryMembersError) throw juryMembersError;

      // "Tus categorías" = solo las del usuario actual
      const assignedIdsMap = (juryMembersRows || []).reduce((acc: Record<string, true>, row: any) => {
        if (row?.user_id === user?.id) {
          acc[row.category_id] = true;
        }
        return acc;
      }, {});
      setAssignedCategoryIds(assignedIdsMap);

      const countsMap = (juryMembersRows || []).reduce((acc: Record<string, number>, row: any) => {
        if (!row?.category_id) return acc;
        acc[row.category_id] = (acc[row.category_id] || 0) + 1;
        return acc;
      }, {});
      setJuryCounts(countsMap);

      // Jurors by category (id list)
      const jurorsByCategory: Record<string, string[]> = {};
      (juryMembersRows || []).forEach((row: any) => {
        if (!row?.category_id) return;
        jurorsByCategory[row.category_id] = jurorsByCategory[row.category_id] || [];
        jurorsByCategory[row.category_id].push(row.user_id);
      });

      // Fetch juror profiles for names
      const allJurorIds = Array.from(new Set((juryMembersRows || []).map((r: any) => r.user_id)));
      const jurorProfilesById: Record<string, { name: string; email: string }> = {};
      if (allJurorIds.length > 0) {
        const { data: profs } = await supabase
          .schema('id')
          .from('users')
          .select('id, name, last_name, email')
          .in('id', allJurorIds);
        (profs || []).forEach((p: any) => {
          jurorProfilesById[p.id] = {
            name: [p.name, p.last_name].filter(Boolean).join(' ').trim() || p.email || '—',
            email: p.email || '',
          };
        });
      }

      // Calculamos el ranking en el cliente para asegurar que aparezcan candidaturas con votos en jury_votes,
      // incluso cuando ya haya sido votada por otros jurados.
      const results = await Promise.all(
        categories.map(async (category) => {
          const categoryId = category.id;
          const totalJurors = countsMap[categoryId] || 0;

          // Solo candidaturas aprobadas (las que pueden estar en votación del jurado)
          const { data: nominationsData, error: nominationsError } = await supabase
            .schema('premios')
            .from('nominations')
            .select('id, candidate_name, project_title, status')
            .eq('category_id', categoryId)
            .eq('status', 'approved');

          if (nominationsError) throw nominationsError;

          const nominations = (nominationsData || []) as Partial<Nomination>[];
          const nominationIds = nominations.map((n) => n.id).filter(Boolean) as string[];
          const categoryJurors = jurorsByCategory[categoryId] || [];

          // Build matrix info (always, even with empty votes)
          const matrixEntry = {
            jurors: categoryJurors.map((id) => ({
              id,
              name: jurorProfilesById[id]?.name || '—',
              email: jurorProfilesById[id]?.email || '',
            })),
            nominations: nominations.map((n) => ({
              id: n.id as string,
              title: n.candidate_name || n.project_title || 'Sin nombre',
            })),
            votes: {} as Record<string, Set<string>>,
          };

          if (nominationIds.length === 0) {
            return {
              categoryId,
              rows: [] as RankingRow[],
              jurorsVoted: 0,
              approvedCount: 0,
              activeJurors: 0,
              matrix: matrixEntry,
            };
          }

          const { data: votesData, error: votesError } = await supabase
            .schema('premios')
            .from('jury_votes')
            .select('*')
            .in('nomination_id', nominationIds);

          if (votesError) throw votesError;

          const votes = (votesData || []) as JuryVote[];

          // Populate matrix votes: nomId -> set of jurorIds that voted it
          votes.forEach((v) => {
            if (!v.nomination_id || !v.jury_member_id) return;
            matrixEntry.votes[v.nomination_id] = matrixEntry.votes[v.nomination_id] || new Set();
            matrixEntry.votes[v.nomination_id].add(v.jury_member_id);
          });

          // "Candidaturas": nominaciones donde TODOS los jurados asignados ya han votado
          const fullyVotedCount = nominationIds.filter((nId) => {
            const jurorsThatVoted = matrixEntry.votes[nId] || new Set();
            return categoryJurors.length > 0 && categoryJurors.every((jId) => jurorsThatVoted.has(jId));
          }).length;

          // "Jurado": de los jurados asignados a esta categoría, cuántos han votado al menos una candidatura
          const votedJurorIds = new Set<string>();
          Object.values(matrixEntry.votes).forEach((set) => {
            set.forEach((jId) => votedJurorIds.add(jId));
          });
          const activeJurors = categoryJurors.filter((jId) => votedJurorIds.has(jId)).length;

          // Agrupamos votos por nomination_id
          const votesByNominationId = votes.reduce(
            (acc: Record<string, JuryVote[]>, v) => {
              if (!v.nomination_id) return acc;
              acc[v.nomination_id] = acc[v.nomination_id] || [];
              acc[v.nomination_id].push(v);
              return acc;
            },
            {}
          );

          const rows = Object.keys(votesByNominationId)
            .map((nominationId) => {
              const nomination = nominations.find((n) => n.id === nominationId);
              const nominationVotes = votesByNominationId[nominationId] || [];

              // total_votes: jurados distintos que votaron
              const distinctJuryMembers = Array.from(
                new Set(nominationVotes.map((v) => v.jury_member_id).filter(Boolean))
              );
              const totalVotes = distinctJuryMembers.length;

              const voteAverages = nominationVotes.map((v) => {
                // Supabase devuelve NUMERIC como string — hay que parsear
                // antes de sumar, si no los valores se descartan como 0.
                const safe = (val?: number | string | null) => {
                  if (val === null || val === undefined) return 0;
                  const n = typeof val === 'number' ? val : parseFloat(val);
                  return isNaN(n) ? 0 : n;
                };
                if (category.type === 'personal') {
                  const a =
                    safe(v.trayectoria_profesional) +
                    safe(v.actividades_destacadas) +
                    safe(v.compromiso_difusion);
                  return a / 3;
                }
                if (category.type === 'benchmark') {
                  const a =
                    safe(v.benchmark_impacto_sostenido) +
                    safe(v.benchmark_replicabilidad) +
                    safe(v.benchmark_adopcion_referencia) +
                    safe(v.benchmark_capacidad_inspirar);
                  return a / 4;
                }
                // project
                const a =
                  safe(v.creatividad_innovacion) +
                  safe(v.impacto_ciudadania) +
                  safe(v.transferencia_difusion) +
                  safe(v.sostenibilidad) +
                  safe(v.cambio_cultura) +
                  safe(v.equilibrio_eficacia) +
                  safe(v.evaluacion_iniciativa);
                return a / 7;
              });

              const average_score =
                voteAverages.length > 0
                  ? voteAverages.reduce((sum, x) => sum + x, 0) / voteAverages.length
                  : 0;

              const points_total = average_score * totalVotes;

              return {
                nomination_id: nominationId,
                candidate_name: nomination?.candidate_name ?? null,
                project_title: nomination?.project_title ?? null,
                average_score,
                total_votes: totalVotes,
                total_jurors: totalJurors,
                points_total,
              } as Omit<RankingRow, 'rank'>;
            })
            // en el ranking solo incluimos las ya votadas (las que existen en jury_votes)
            .filter((r): r is Omit<RankingRow, 'rank'> => (r.total_votes || 0) > 0)
            // Orden por puntos totales desc. Supabase no garantiza el orden de
            // las filas sin ORDER BY, así que en empates exactos el resultado
            // de un .sort() sin desempate explícito puede variar entre cargas
            // de la página. Desempatamos por nomination_id (igual que la RPC
            // get_public_voting_finalists) para que el resultado sea siempre
            // el mismo, sin importar en qué orden lleguen las filas.
            .sort((a, b) => b.points_total - a.points_total || a.nomination_id.localeCompare(b.nomination_id))
            .map((r, idx) => ({
              ...(r as any),
              rank: idx + 1,
            })) as RankingRow[];

          return {
            categoryId,
            rows,
            jurorsVoted: fullyVotedCount,
            approvedCount: nominations.length,
            activeJurors,
            matrix: matrixEntry,
          };
        })
      );

      const matrixMap = results.reduce((acc: any, r: any) => {
        if (r.matrix) acc[r.categoryId] = r.matrix;
        return acc;
      }, {});
      setMatrixByCategory(matrixMap);

      const rankingMap = results.reduce((acc: Record<string, RankingRow[]>, r) => {
        acc[r.categoryId] = r.rows;
        return acc;
      }, {});

      const jurorsVotedMap = results.reduce((acc: Record<string, number>, r) => {
        acc[r.categoryId] = r.jurorsVoted;
        return acc;
      }, {});
      setJurorsVotedByCategory(jurorsVotedMap);

      const approvedMap = results.reduce((acc: Record<string, number>, r) => {
        acc[r.categoryId] = r.approvedCount;
        return acc;
      }, {});
      setApprovedCountByCategory(approvedMap);

      const activeJurorsMap = results.reduce((acc: Record<string, number>, r) => {
        acc[r.categoryId] = r.activeJurors;
        return acc;
      }, {});
      setActiveJurorsByCategory(activeJurorsMap);

      setRankings(rankingMap);

      // Public votes count per nomination — agregado en el servidor (RPC),
      // no filas en crudo: por defecto PostgREST corta cada respuesta a
      // 1000 filas, y con más de 1000 votos el conteo salía truncado.
      const { data: voteCounts } = await supabase.schema('premios').rpc('get_public_vote_counts');
      const pubCounts: Record<string, number> = {};
      ((voteCounts?.counts || []) as { nomination_id: string; votes: number }[]).forEach((row) => {
        pubCounts[row.nomination_id] = row.votes;
      });
      setPublicVotesByNomination(pubCounts);
      setTotalUniqueVoters(voteCounts?.total_unique_voters || 0);
    } catch (error) {
      console.error('Error loading jury rankings:', error);
      setRankings({});
      setJuryCounts({});
    } finally {
      setLoading(false);
    }
  };

  if (!canViewRankings) {
    return (
      <div className="container dashboard">
        <div className="empty-state">
          <h3>Solo disponible para miembros del jurado</h3>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (myCategories.length === 0) {
    return (
      <div className="container dashboard">
        <div className="empty-state">
          <h3>No hay categorías disponibles</h3>
        </div>
      </div>
    );
  }


  const downloadChartPng = (svgId: string, filename: string) => {
    const svg = document.getElementById(svgId) as SVGSVGElement | null;
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // Convert white/translucent strokes and text fills to black for export
    clone.querySelectorAll('text').forEach((el) => el.setAttribute('fill', '#000'));
    clone.querySelectorAll('line').forEach((el) => {
      const s = el.getAttribute('stroke') || '';
      // Grid lines (más claras) → gris muy suave; ejes → negro
      if (s.includes('0.08')) {
        el.setAttribute('stroke', 'rgba(0,0,0,0.08)');
      } else {
        el.setAttribute('stroke', '#000');
      }
    });
    const w = Number(svg.getAttribute('width')) || svg.clientWidth;
    const h = Number(svg.getAttribute('height')) || svg.clientHeight;
    const serialized = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement('a');
        const pngUrl = URL.createObjectURL(blob);
        a.href = pngUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    };
    img.src = url;
  };

  return (
    <div className="container dashboard">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0 }}>Votaciones</h1>
          <p style={{ margin: '4px 0 0' }}>
            {view === 'charts'
              ? 'Gráficas de puntuación media por candidatura'
              : view === 'finalists'
              ? 'Podio de finalistas y votos del público'
              : 'Ranking de candidaturas por categoría jurado'}
          </p>
          {view === 'finalists' && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                padding: '4px 12px',
                borderRadius: 999,
                background: 'rgba(255,199,0,0.1)',
                border: '1px solid rgba(255,199,0,0.3)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--color-secondary)',
              }}
            >
              {totalUniqueVoters} persona{totalUniqueVoters === 1 ? '' : 's'} ha{totalUniqueVoters === 1 ? '' : 'n'} votado
            </span>
          )}
          {view === 'finalists' && (() => {
            const totalVotes = Object.values(publicVotesByNomination).reduce((a, b) => a + b, 0);
            return (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                  marginLeft: 8,
                  padding: '4px 12px',
                  borderRadius: 999,
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#34d399',
                }}
              >
                {totalVotes} voto{totalVotes === 1 ? '' : 's'} en total
              </span>
            );
          })()}
        </div>
        <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 999, padding: 4, gap: 4 }}>
          <button
            onClick={() => setView('rankings')}
            style={{
              padding: '8px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
              background: view === 'rankings' ? 'var(--color-secondary)' : 'transparent',
              color: view === 'rankings' ? '#000' : 'var(--color-text)',
              fontWeight: 600, fontSize: 13,
            }}
          >Ranking</button>
          <button
            onClick={() => setView('charts')}
            style={{
              padding: '8px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
              background: view === 'charts' ? 'var(--color-secondary)' : 'transparent',
              color: view === 'charts' ? '#000' : 'var(--color-text)',
              fontWeight: 600, fontSize: 13,
            }}
          >Gráficas</button>
          <button
            onClick={() => setView('finalists')}
            style={{
              padding: '8px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
              background: view === 'finalists' ? 'var(--color-secondary)' : 'transparent',
              color: view === 'finalists' ? '#000' : 'var(--color-text)',
              fontWeight: 600, fontSize: 13,
            }}
          >Finalistas</button>
        </div>
      </div>

      {view === 'rankings' && (() => {
        const assignedSet = assignedCategoryIds;
        const sorted = [...myCategories].sort((a, b) => {
          const aAssigned = !!assignedSet[a.id];
          const bAssigned = !!assignedSet[b.id];
          if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;
          return a.name.localeCompare(b.name, 'es');
        });

        const firstAssignedIndex = sorted.findIndex((c) => !!assignedSet[c.id]);
        const lastAssignedIndex =
          sorted.map((c, idx) => (assignedSet[c.id] ? idx : -1)).filter((idx) => idx >= 0).pop() ?? -1;

        return sorted.map((category, idx) => {
          const rows = rankings[category.id] || [];
          const totalJurors = juryCounts[category.id] || 0;
          const hasVotes = rows.length > 0;

          const showParticipaDivider = idx === firstAssignedIndex;
          const showOtrasDivider = lastAssignedIndex >= 0 && idx === lastAssignedIndex + 1;

          return (
            <React.Fragment key={category.id}>
              {showParticipaDivider && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    margin: '26px 0 14px',
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: 'rgba(255, 215, 0, 0.25)' }} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--color-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Tus categorías
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255, 215, 0, 0.25)' }} />
                </div>
              )}

              {showOtrasDivider && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    margin: '26px 0 14px',
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: 'rgba(255, 255, 255, 0.12)' }} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Otras categorías
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255, 255, 255, 0.12)' }} />
                </div>
              )}

              <div className="card" style={{ marginTop: '24px' }}>
            <div className="category-header">
              <h2>{category.name}</h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span
                  className="badge badge-approved"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setMatrixCategory(category)}
                  title="Ver matriz de votación"
                >
                  Jurado: {activeJurorsByCategory[category.id] || 0}/{juryCounts[category.id] || 0}
                </span>
                <span
                  className="badge badge-approved"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setMatrixCategory(category)}
                  title="Ver matriz de votación"
                >
                  Candidaturas: {jurorsVotedByCategory[category.id] || 0}/{approvedCountByCategory[category.id] || 0}
                </span>
              </div>
            </div>

              {!hasVotes ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <p style={{ color: 'var(--color-text-secondary)' }}>
                    Todavía no se han registrado votos para esta categoría.
                  </p>
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Posición</th>
                      <th>Candidatura</th>
                      <th>Puntos totales</th>
                      <th>Media</th>
                      <th>Votos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const votes = row.total_votes || 0;
                      const avg = row.average_score || 0;
                      const totalPoints = row.points_total || 0;
                      return (
                        <tr
                          key={row.nomination_id}
                          style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            const { data } = await supabase
                              .schema('premios')
                              .from('nominations')
                              .select('*')
                              .eq('id', row.nomination_id)
                              .single();
                            if (data) setSelectedNomination(data);
                          }}
                        >
                          <td>
                            <strong>#{row.rank}</strong>
                          </td>
                          <td>{row.candidate_name || row.project_title}</td>
                          <td>{totalPoints.toFixed(2)}</td>
                          <td>{avg.toFixed(2)}</td>
                          <td>
                            <span
                              style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}
                              title="Ver quién ha evaluado esta candidatura"
                              onClick={(e) => {
                                e.stopPropagation();
                                const matrix = matrixByCategory[category.id];
                                const votedIds = matrix?.votes[row.nomination_id] || new Set<string>();
                                const jurors = matrix?.jurors || [];
                                setVoteVotersModal({
                                  title: row.candidate_name || row.project_title || '—',
                                  voted: jurors.filter((j) => votedIds.has(j.id)),
                                  pending: jurors.filter((j) => !votedIds.has(j.id)),
                                });
                              }}
                            >
                              {votes}/{row.total_jurors || totalJurors || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            </React.Fragment>
          );
        });
      })()}

      {view === 'charts' && (() => {
        const sorted = [...myCategories].sort((a, b) => a.name.localeCompare(b.name, 'es'));
        const categoriesWithData = sorted.filter((c) => (rankings[c.id] || []).length > 0);
        if (categoriesWithData.length === 0) {
          return (
            <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: 40 }}>
              <p style={{ color: 'var(--color-text-secondary)' }}>Aún no hay puntuaciones para mostrar.</p>
            </div>
          );
        }
        return categoriesWithData.map((category) => {
          const rows = (rankings[category.id] || []).slice().sort((a, b) => b.average_score - a.average_score);
          const labelFor = (r: RankingRow) => (r.candidate_name || r.project_title || '—');
          // Chart geometry
          const W = Math.max(560, rows.length * 90);
          const H = 320;
          const padL = 50, padR = 20, padT = 20, padB = 90;
          const chartW = W - padL - padR;
          const chartH = H - padT - padB;
          const maxScore = rows.reduce((m, r) => Math.max(m, r.average_score), 0);
          const yMax = Math.max(1, Math.ceil(maxScore + 1));
          const tickStep = yMax <= 5 ? 1 : yMax <= 10 ? 2 : Math.ceil(yMax / 5);
          const yTicks: number[] = [];
          for (let t = 0; t <= yMax; t += tickStep) yTicks.push(t);
          if (yTicks[yTicks.length - 1] !== yMax) yTicks.push(yMax);
          const barW = Math.min(60, (chartW / rows.length) * 0.65);
          const step = chartW / rows.length;
          const BAR_COLORS = ['#ffc700', '#2a9df4'];
          const colorFor = (_score: number, idx: number) => BAR_COLORS[idx % BAR_COLORS.length];
          return (
            <div key={category.id} className="card" style={{ marginTop: 24, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
                <h2 style={{ margin: 0 }}>{category.name}</h2>
                <button
                  onClick={() => downloadChartPng(`chart-${category.id}`, `grafica-${category.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`)}
                  title="Descargar gráfica como PNG"
                  aria-label="Descargar PNG"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 36, height: 36, borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'var(--color-secondary)', cursor: 'pointer',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <svg id={`chart-${category.id}`} width={W} height={H} style={{ display: 'block' }}>
                  {/* Y grid + labels */}
                  {yTicks.map((t) => {
                    const y = padT + chartH - (t / yMax) * chartH;
                    return (
                      <g key={t}>
                        <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="rgba(255,255,255,0.08)" />
                        <text x={padL - 8} y={y + 4} textAnchor="end" fill="rgba(255,255,255,0.55)" fontSize={11}>{t}</text>
                      </g>
                    );
                  })}
                  {/* Y axis title */}
                  <text x={12} y={padT + chartH / 2} fill="rgba(255,255,255,0.6)" fontSize={11} transform={`rotate(-90 12 ${padT + chartH / 2})`} textAnchor="middle">Puntuación media</text>
                  {/* Bars */}
                  {rows.map((r, i) => {
                    const cx = padL + step * i + step / 2;
                    const h = (r.average_score / yMax) * chartH;
                    const x = cx - barW / 2;
                    const y = padT + chartH - h;
                    const label = labelFor(r);
                    const short = label.length > 14 ? label.slice(0, 13) + '…' : label;
                    return (
                      <g key={r.nomination_id}>
                        <rect x={x} y={y} width={barW} height={h} rx={4} fill={colorFor(r.average_score, i)}>
                          <title>{`${label}: ${r.average_score.toFixed(2)} (${r.total_votes}/${r.total_jurors} jurados)`}</title>
                        </rect>
                        <text x={cx} y={y - 6} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>{r.average_score.toFixed(1)}</text>
                        <text x={cx} y={padT + chartH + 14} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize={11} transform={`rotate(-30 ${cx} ${padT + chartH + 14})`}>{short}</text>
                      </g>
                    );
                  })}
                  {/* Axes */}
                  <line x1={padL} x2={W - padR} y1={padT + chartH} y2={padT + chartH} stroke="rgba(255,255,255,0.25)" />
                  <line x1={padL} x2={padL} y1={padT} y2={padT + chartH} stroke="rgba(255,255,255,0.25)" />
                </svg>
              </div>
            </div>
          );
        });
      })()}

      {view === 'finalists' && (() => {
        const sorted = [...myCategories].sort((a, b) => a.name.localeCompare(b.name, 'es'));
        const categoriesWithData = sorted.filter((c) => (rankings[c.id] || []).length > 0);
        if (categoriesWithData.length === 0) {
          return (
            <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: 40 }}>
              <p style={{ color: 'var(--color-text-secondary)' }}>Aún no hay finalistas para mostrar.</p>
            </div>
          );
        }

        // Podium heights and colors for positions 1,2,3
        const PODIUM = {
          1: { height: 160, bg: 'linear-gradient(180deg, #ffd700 0%, #c99a00 100%)', ring: '#ffd700', medal: '🥇' },
          2: { height: 120, bg: 'linear-gradient(180deg, #d9d9d9 0%, #8a8a8a 100%)', ring: '#d9d9d9', medal: '🥈' },
          3: { height: 90,  bg: 'linear-gradient(180deg, #cd7f32 0%, #8a5a1f 100%)', ring: '#cd7f32', medal: '🥉' },
        } as const;

        return categoriesWithData.map((category) => {
          // Los 3 finalistas son los de mayor media del jurado (los que aparecen en la votación
          // pública). Desempate por nomination_id: mismo criterio exacto que la RPC
          // get_public_voting_finalists (la que de verdad decide quién es votable), para que
          // ante un empate de medias esta vista muestre siempre a la misma candidatura como
          // finalista, en vez de que varíe según el orden en que lleguen las filas de Supabase.
          const finalists = (rankings[category.id] || [])
            .slice()
            .sort((a, b) => b.average_score - a.average_score || a.nomination_id.localeCompare(b.nomination_id))
            .slice(0, 3);

          // El podio se ordena por VOTOS DEL PÚBLICO (desempate: media jurado, luego nomination_id)
          const top3 = finalists.slice().sort((a, b) => {
            const va = publicVotesByNomination[a.nomination_id] || 0;
            const vb = publicVotesByNomination[b.nomination_id] || 0;
            if (vb !== va) return vb - va;
            if (b.average_score !== a.average_score) return b.average_score - a.average_score;
            return a.nomination_id.localeCompare(b.nomination_id);
          });

          // Orden visual: 2º, 1º, 3º
          const visualOrder = [top3[1], top3[0], top3[2]].filter(Boolean) as RankingRow[];

          const totalPublicVotes = top3.reduce(
            (sum, r) => sum + (publicVotesByNomination[r.nomination_id] || 0),
            0
          );

          return (
            <div key={category.id} className="card" style={{ marginTop: 24, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>{category.name}</h2>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {totalPublicVotes} voto{totalPublicVotes === 1 ? '' : 's'} del público
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: 18,
                  padding: '32px 16px 0',
                  flexWrap: 'wrap',
                }}
              >
                {visualOrder.map((row) => {
                  const rank = top3.findIndex((r) => r.nomination_id === row.nomination_id) + 1;
                  const pos = rank as 1 | 2 | 3;
                  const meta = PODIUM[pos];
                  const title = row.candidate_name || row.project_title || '—';
                  const votes = publicVotesByNomination[row.nomination_id] || 0;
                  const share = totalPublicVotes > 0 ? Math.round((votes / totalPublicVotes) * 100) : 0;

                  return (
                    <div
                      key={row.nomination_id}
                      onClick={async () => {
                        const { data } = await supabase
                          .schema('premios')
                          .from('nominations')
                          .select('*')
                          .eq('id', row.nomination_id)
                          .single();
                        if (data) setSelectedNomination(data);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 10,
                        width: 200,
                        cursor: 'pointer',
                      }}
                    >
                      {/* Tarjeta del finalista */}
                      <div
                        style={{
                          width: '100%',
                          padding: '14px 12px',
                          borderRadius: 10,
                          background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${meta.ring}55`,
                          textAlign: 'center',
                          minHeight: 110,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: 6,
                        }}
                      >
                        <div style={{ fontSize: 28, lineHeight: 1 }}>{meta.medal}</div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: '#fff',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                          title={title}
                        >
                          {title}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          Media jurado: <strong style={{ color: '#fff' }}>{row.average_score.toFixed(2)}</strong>
                        </div>
                      </div>

                      {/* Número de votos público destacado */}
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: meta.ring,
                          lineHeight: 1,
                          marginTop: 4,
                        }}
                      >
                        {votes}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: -4 }}>
                        voto{votes === 1 ? '' : 's'} · {share}%
                      </div>

                      {/* Bloque del podio */}
                      <div
                        style={{
                          width: '100%',
                          height: meta.height,
                          borderRadius: '8px 8px 0 0',
                          background: meta.bg,
                          boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          paddingTop: 10,
                          color: '#1a1a1a',
                          fontWeight: 900,
                          fontSize: 28,
                          letterSpacing: 1,
                          textShadow: '0 1px 2px rgba(255,255,255,0.3)',
                        }}
                      >
                        {pos}º
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        });
      })()}

      {matrixCategory && (() => {
        const m = matrixByCategory[matrixCategory.id];
        if (!m) return null;
        return (
          <div className="modal-overlay" onClick={() => setMatrixCategory(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>Matriz de votación · {matrixCategory.name}</h2>
                <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '6px 14px' }} onClick={() => setMatrixCategory(null)}>Cerrar</button>
              </div>
              {m.jurors.length === 0 ? (
                <div className="empty-state"><p>No hay jurado asignado a esta categoría.</p></div>
              ) : m.nominations.length === 0 ? (
                <div className="empty-state"><p>No hay candidaturas aprobadas en esta categoría.</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: '180px' }}>Candidatura</th>
                        {m.jurors.map((j) => (
                          <th
                            key={j.id}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={async () => {
                              setSelectedJuror(j);
                              const { data } = await supabase
                                .schema('id')
                                .from('users')
                                .select('*')
                                .eq('id', j.id)
                                .single();
                              setSelectedJurorProfile(data);
                            }}
                            title="Ver perfil del jurado"
                          >
                            {j.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {m.nominations.map((nom) => (
                        <tr
                          key={nom.id}
                          style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            const { data } = await supabase
                              .schema('premios')
                              .from('nominations')
                              .select('*')
                              .eq('id', nom.id)
                              .single();
                            if (data) setSelectedNomination(data);
                          }}
                        >
                          <td>{nom.title}</td>
                          {m.jurors.map((j) => {
                            const voted = m.votes[nom.id]?.has(j.id);
                            return (
                              <td
                                key={j.id}
                                style={{
                                  textAlign: 'center',
                                  color: voted ? 'var(--color-success)' : 'var(--color-error)',
                                  fontWeight: 700,
                                }}
                              >
                                {voted ? '✓ Evaluado' : '✗ No evaluado'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {selectedJuror && (
        <div
          className="modal-overlay"
          onClick={() => { setSelectedJuror(null); setSelectedJurorProfile(null); }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>Perfil del jurado</h2>
              <button
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px' }}
                onClick={() => { setSelectedJuror(null); setSelectedJurorProfile(null); }}
              >
                Cerrar
              </button>
            </div>
            {selectedJurorProfile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedJurorProfile.avatar && (
                  <img
                    src={selectedJurorProfile.avatar}
                    alt=""
                    style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
                <div><strong>Nombre:</strong> {[selectedJurorProfile.name, selectedJurorProfile.last_name].filter(Boolean).join(' ')}</div>
                <div><strong>Email:</strong> {selectedJurorProfile.email}</div>
                {selectedJurorProfile.position && <div><strong>Posición:</strong> {selectedJurorProfile.position}</div>}
                {selectedJurorProfile.institution && <div><strong>Departamento:</strong> {selectedJurorProfile.institution}</div>}
                {selectedJurorProfile.administration_type && <div><strong>Tipo de Administración:</strong> {selectedJurorProfile.administration_type}</div>}
                {selectedJurorProfile.job_category && <div><strong>Categoría de puesto:</strong> {selectedJurorProfile.job_category}</div>}
                {selectedJurorProfile.country && <div><strong>País:</strong> {selectedJurorProfile.country}{selectedJurorProfile.region ? ` · ${selectedJurorProfile.region}` : ''}</div>}
              </div>
            ) : (
              <div className="loading"><div className="spinner" /></div>
            )}
          </div>
        </div>
      )}

      {voteVotersModal && (
        <div className="modal-overlay" onClick={() => setVoteVotersModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>{voteVotersModal.title}</h2>
              <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '6px 14px' }} onClick={() => setVoteVotersModal(null)}>Cerrar</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <p style={{ margin: '0 0 8px', fontWeight: 700, color: 'var(--color-success)' }}>
                  Ya evaluada por ({voteVotersModal.voted.length})
                </p>
                {voteVotersModal.voted.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Nadie la ha evaluado todavía.</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    {voteVotersModal.voted.map((j) => (
                      <li key={j.id}>{j.name}{j.email ? ` (${j.email})` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p style={{ margin: '0 0 8px', fontWeight: 700, color: 'var(--color-error)' }}>
                  Pendiente de evaluar ({voteVotersModal.pending.length})
                </p>
                {voteVotersModal.pending.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Todo el jurado asignado ya ha votado.</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    {voteVotersModal.pending.map((j) => (
                      <li key={j.id}>{j.name}{j.email ? ` (${j.email})` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedNomination && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedNomination(null)}
          style={{ zIndex: 2000 }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <NominationDetails
              nomination={selectedNomination}
              category={myCategories.find((c) => c.id === selectedNomination.category_id)}
              onClose={() => setSelectedNomination(null)}
              showActions={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JuryVotes;

